// Страница „Ново изписване" (/inventory/writeoff/new) — празен работен лист
// или продължената чернова. Бързото начало на началния екран подава вида и
// причината за брак през router state `{preset}`; черновата от сървъра идва с
// `{draftId, ownDraft}` (листът чака зареждането ѝ), а директно отваряне пита
// „Да продължите ли незавършеното изписване?", ако има моя (`useDraftResume`).
// „Продължи" монтира листа наново с черновата (`version`) — редовете и
// партидите му живеят в неговите hook-ове. Записът минава през
// useCreateWriteoff, а успехът маха черновата и връща към началния екран със
// `state.openDoc` (toast „Виж документа").
import { useMemo, useState } from "react";
import { App, Spin } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCreateWriteoff, useProducts } from "../../../hooks/useInventory";
import type { CreateWriteoffPayload, Writeoff } from "../../../api/inventory";
import type { DraftRouterState } from "../components/drafts/draftModel";
import { useDraftResume } from "../components/drafts/useDraftResume";
import { DraftResumePrompt } from "../components/drafts/DraftResumePrompt";
import { DRAFT_TEXTS } from "../components/drafts/draftTexts";
import { WriteoffWorksheet } from "./components/WriteoffWorksheet";
import { writeoffPreviewItems } from "./components/useWriteoffLeave";
import {
  WRITEOFF_DRAFT_STORE,
  hasWriteoffDraftContent,
  writeoffInitialOfDraft,
  type WriteoffDraft,
} from "./components/writeoffDraft";
import {
  AUTO_SCRAP_REASON,
  emptyWorksheetInitial,
  type WriteoffPreset,
  type WriteoffWorksheetInitial,
} from "./components/writeoffWorksheetShared";

const HOME_PATH = "/inventory/writeoff";

type Props = {
  warehouseId?: number | null;
  onRegisterLeaveGuard?: (guard: ((proceed: () => void) => void) | null) => void;
};

type RouterState = (DraftRouterState & { preset?: WriteoffPreset }) | null;

// Видът/причината от бързото начало. Запазената причина на автоматичния брак
// НЕ се приема — тя залостява листа (виж AUTO_SCRAP_REASON).
const withPreset = (
  initial: WriteoffWorksheetInitial,
  preset: WriteoffPreset | undefined,
): WriteoffWorksheetInitial =>
  preset && (preset.kind === "writeoff" || preset.kind === "scrap") && preset.reason !== AUTO_SCRAP_REASON
    ? { ...initial, kind: preset.kind, reason: preset.reason ?? "" }
    : initial;

export const WriteoffCreatePage = ({ warehouseId = null, onRegisterLeaveGuard }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const createMutation = useCreateWriteoff();
  const { data: products = [] } = useProducts();
  const productById = useMemo(() => new Map(products.map((product) => [product.id, product])), [products]);
  const routerState = (location.state as RouterState) ?? null;

  // Листът: началното състояние (взето веднъж; складът от конфигурацията се
  // попълва отложено през defaultWarehouseId) и черновата, от която тръгва
  const [sheet, setSheet] = useState(() => ({
    version: 0,
    initial: withPreset(emptyWorksheetInitial(warehouseId), routerState?.preset),
    draft: { draftId: null as number | null, own: true },
  }));
  // Избрана на началния екран чернова — листът се показва чак с нея
  const [waitingForDraft, setWaitingForDraft] = useState(
    routerState?.draftId != null || routerState?.draftChoice === "continue",
  );

  const resume = useDraftResume<WriteoffDraft>({
    type: "writeoff",
    enabled: true,
    routerState,
    local: WRITEOFF_DRAFT_STORE,
    hasContent: hasWriteoffDraftContent,
    onApply: ({ payload, draftId, own }) =>
      setSheet((current) => ({
        version: current.version + 1,
        initial: writeoffInitialOfDraft(payload, warehouseId),
        draft: { draftId, own },
      })),
    onSettled: () => setWaitingForDraft(false),
    onLoadError: () => {
      message.error(t("inventoryPage.errLoad"));
      navigate(HOME_PATH);
    },
  });

  const handleSave = (payload: CreateWriteoffPayload, onSaved: () => void) => {
    createMutation.mutate(payload, {
      onSuccess: (saved: Writeoff) => {
        onSaved();
        navigate(HOME_PATH, { state: { openDoc: saved } });
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { detail?: string } } };
        message.error(err.response?.data?.detail || t("inventoryPage.errWriteoff"));
      },
    });
  };

  if (waitingForDraft) {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <Spin size="large" />
      </div>
    );
  }

  // „Продукт — количество" на попълнените редове на намерената чернова
  const pendingItems = writeoffPreviewItems(t, resume.pending?.payload.lines ?? [], productById);
  return (
    <>
      <WriteoffWorksheet
        key={sheet.version}
        mode="new"
        title={t("inventoryPage.newWriteoff")}
        initial={sheet.initial}
        draft={sheet.draft}
        saving={createMutation.isPending}
        onSave={handleSave}
        defaultWarehouseId={warehouseId}
        onRegisterLeaveGuard={onRegisterLeaveGuard}
      />
      <DraftResumePrompt
        open={resume.pending != null}
        texts={DRAFT_TEXTS.writeoff}
        savedAt={resume.pending?.payload.savedAt ?? null}
        savedBy={resume.pending?.payload.savedBy ?? null}
        items={pendingItems.slice(0, 5)}
        total={pendingItems.length}
        onContinue={resume.continueDraft}
        onDiscard={resume.discardDraft}
        onDismiss={resume.dismiss}
      />
    </>
  );
};

export default WriteoffCreatePage;
